import type { TCDCapacity, TCDData } from '~/src/types/capacity-database'
import { Decimal } from 'decimal.js'
import { numberFormatter } from '~/utils/number'
import type { TGeneral } from '~/src/types/common'
import { EventBus } from '@/utils/eventBus'

export const capacityDabaBaseTitle = (market: string) =>
  `${market} Capacity Database`
export const capacityDabaBaseSubTitle = (market: string) =>
  `${market} Capacities`

export const getYears = (capacities: TCDCapacity[]) => {
  return capacities
    .map((capacity) => capacity.label)
    .sort((a, b) => a.localeCompare(b))
}

export const getYearStates = (_years: string[]) => {
  const _yearStates = {}
  _years.forEach((_year) => (_yearStates[_year] = true))
  _yearStates[_years[_years.length - 1]] = false
  return _yearStates
}

export const getLastYear = (_years: string[] | null) => _years[2]?.split(' ')[0]

export const getOptionsChart = (
  unit: string,
  hideYLabel: boolean,
  truncateLabels: boolean,
  substrLength: number,
  color: string,
  labels: string[],
) => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    tooltips: {
      callbacks: {
        label: function label(tooltipItem: TGeneral, data: TCDData) {
          const currentValueIndex = tooltipItem.index
          const currentYearIndex = tooltipItem.datasetIndex
          const currentYear = data.datasets[tooltipItem.datasetIndex].label
          const unit = data.datasets[tooltipItem.datasetIndex].unit
          const decimalPlaces =
            data.datasets[tooltipItem.datasetIndex].decimalPlaces
          let currentValue = new Decimal(0)
          // Get accumulated capacity per year
          for (let i = 0; i <= currentYearIndex; i++) {
            const yearCapacityValue = new Decimal(
              data.datasets[i].data[currentValueIndex],
            )
            currentValue = currentValue.plus(yearCapacityValue)
          }
          return `${currentYear}: ${numberFormatter(
            currentValue,
            decimalPlaces,
          )} ${unit}`
        },
      },
    },
    scales: {
      x: {
        position: 'top',
        stacked: true,
        min: 0,
        title: {
          display: true,
          font: {
            family: 'Montserrat',
            size: 16,
          },
          text: unit,
        },
        ticks: {
          padding: 10,
          autoSkip: false,
          maxRotation: 90,
          minRotation: 90,
          font: {
            family: 'Montserrat',
            size: 16,
            color,
          },
        },
      },

      y: {
        display: !hideYLabel,
        stacked: true,
        ticks: {
          font: {
            family: 'Montserrat',
            size: 16,
            color,
          },
          callback: function (index: number) {
            const fullLabel = labels[index]
            if (truncateLabels && fullLabel?.length > substrLength) {
              return `${fullLabel.substring(0, substrLength)}...`
            }
            return fullLabel
          },
        },
      },
    },

    onHover: function (event: MouseEvent) {
      EventBus.$emit('on-capacity-database-hover', event)
    },

    plugins: {
      legend: {
        display: false,
      },
      htmlLegend: {
        containerID: 'legend-container',
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        callbacks: {
          label(context: TGeneral) {
            const { label, unit, decimalPlaces } = context.dataset
            const currentValueIndex = context.dataIndex
            const currentYearIndex = context.datasetIndex
            let currentValue = new Decimal(0)
            // Get accumulated capacity per year
            for (let i = 0; i <= currentYearIndex; i++) {
              const yearCapacityValue = new Decimal(
                context.chart.data.datasets[i].data[currentValueIndex],
              )
              currentValue = currentValue.plus(yearCapacityValue)
            }
            return `${label}: ${numberFormatter(
              currentValue,
              decimalPlaces,
            )} ${unit}`
          },
        },
      },
    },
  }
}
